"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { clsx } from "clsx";
import { type Key } from "react-aria-components";
import { useTranslations } from "next-intl";
import { APIDemo, TextInput } from "../APIDemo";
import {
  createAudio,
  type CreateAudioError,
  type CreateAudioReturn,
} from "@/app/_blocks/BlockInteractCreateOmnibox/createAudio";
import { appUrls } from "@/constants";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { IconExclamationTriangle } from "@/app/_blocks/BlockInteractCreateOmnibox/Error";
import { Footer } from "@/app/_blocks/BlockInteractCreateOmnibox/Footer";
import { useSpinDelay } from "spin-delay";
import {
  generateMusic,
  type Track,
  type MusicGenerationError,
} from "@/app/_blocks/BlockInteractCreateOmnibox/generateMusic";
import type { CodeToken } from "@ui/lib/syntaxHighlighting";

function IconAudio(props: React.ComponentPropsWithoutRef<"svg">) {
  return (
    <svg viewBox='0 0 24 24' {...props}>
      <path
        fill='none'
        stroke='currentColor'
        strokeLinejoin='round'
        strokeWidth='0.125rem'
        d='M9.75 18.75c0 1.38-1.343 2.5-3 2.5s-3-1.12-3-2.5 1.343-2.5 3-2.5 3 1.12 3 2.5Zm0 0V7.26a2 2 0 0 1 1.45-1.923l6.503-1.858a2 2 0 0 1 2.55 1.923v10.35m0 0c0 1.38-1.344 2.5-3 2.5-1.658 0-3-1.12-3-2.5 0-1.382 1.342-2.5 3-2.5 1.656 0 3 1.118 3 2.5Z'
      />
    </svg>
  );
}

type MusicError = Exclude<MusicGenerationError, "generate_aborted"> | CreateAudioError | "empty_prompt";

export function MusicAPI({
  code,
  initialText,
}: {
  initialText: string;
  code: {
    ts: { code: string; quotes: "single" | "double"; tokens: CodeToken[][] };
    python: { code: string; quotes: "single" | "double"; tokens: CodeToken[][] };
  };
}) {
  const [prompt, setPrompt] = useState(initialText);
  const [codeLanguage, setCodeLanguage] = useState<Key>("ts");
  const [state, setState] = useState<"idle" | "generating">("idle");
  const [audioState, setAudioState] = useState<"idle" | "playing" | "loading">("idle");
  const showAudioLoading = useSpinDelay(audioState === "loading", { delay: 200, minDuration: 500 });
  const [error, setError] = useState<MusicError | null>(null);
  const showLimitReachedOverlay = error === "quota_exceeded";
  const [track, setTrack] = useState<Track | null>(null);
  const [regenerate, setRegenerate] = useState(true);

  const audioRef = useRef<CreateAudioReturn | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const t = useTranslations("ClientKeys.BlockInteractCreateOmnibox");
  const tMusicDemo = useTranslations("ClientKeys.music_demo");

  const isGenerating = state === "generating";
  const continueLink = track ? `${appUrls.music}?claimSongId=${track.id}` : appUrls.music;

  const cancelGeneration = useCallback(() => {
    controllerRef.current?.abort("");
    controllerRef.current = null;
  }, []);

  const cleanupAudio = useCallback(() => {
    audioRef.current?.cleanup();
    audioRef.current = null;
  }, []);

  const cleanupAll = useCallback(() => {
    cleanupAudio();
    cancelGeneration();
  }, [cancelGeneration, cleanupAudio]);

  function playTrack(trackToPlay: Track) {
    setError(null);

    if (audioRef.current && audioRef.current.getSrc() !== trackToPlay.src) {
      cleanupAll();
    }

    captureEvent(EventName.marketing_website_music_demo_play_sample, {
      type: "generated",
    });

    setAudioState("loading");

    if (!audioRef.current) {
      audioRef.current = createAudio({
        src: trackToPlay.src,
        onLoaded: () => {
          audioRef.current?.play();
        },
        onPlayStarted: () => {
          setAudioState("playing");
        },
        onMetadata: ({ duration }) => {
          setTrack(prev => {
            if (prev && prev.duration === null) {
              return { ...prev, duration };
            }
            return prev;
          });
        },
        onEnded: () => {
          setAudioState("idle");
        },
        onError: err => {
          setError(err);
          setAudioState("idle");
          cleanupAll();
        },
      });
    }

    if (audioRef.current.isLoaded()) {
      audioRef.current.play();
    } else {
      audioRef.current.load();
    }
  }

  async function handlePrimaryButtonClick() {
    if (regenerate || !track) {
      if (prompt.trim() === "") {
        setError("empty_prompt");
        return;
      }

      setError(null);
      setState("generating");

      const controller = new AbortController();
      controllerRef.current = controller;
      const [error, newTrack] = await generateMusic({
        prompt: prompt.trim(),
        signal: controller.signal,
        source: "api_omnibox",
      });
      if (controller.signal.aborted) {
        setState("idle");
        return;
      }

      if (newTrack) {
        setError(null);
        setRegenerate(false);
        setTrack(newTrack);
        setState("idle");
        playTrack(newTrack);
      } else if (error !== "generate_aborted") {
        setError(error);
        setState("idle");
        cleanupAll();
      } else {
        setState("idle");
      }
      return;
    }

    if (audioState === "playing") {
      setAudioState("idle");
      audioRef.current?.pause();
      return;
    }

    if (audioState === "loading") {
      setAudioState("idle");
      cleanupAudio();
      return;
    }

    if (audioState === "idle" && track) {
      playTrack(track);
    }
  }

  useEffect(() => {
    return cleanupAll;
  }, [cleanupAll]);

  const onChangePrompt = useCallback(
    (value: string) => {
      setPrompt(value);
      setError(null);
      setRegenerate(true);
      cleanupAll();
      setTrack(null);
      setAudioState("idle");
    },
    [cleanupAll],
  );

  const onChangeCodeLanguage = useCallback((value: Key | null) => {
    if (value !== null) {
      setCodeLanguage(value);
    }
  }, []);

  const apidemoState: "idle" | "loading" | "playing" | "paused" =
    isGenerating || showAudioLoading
      ? "loading"
      : regenerate
        ? "idle"
        : audioState === "playing"
          ? "playing"
          : track
            ? "paused"
            : "idle";

  const primaryButtonIsDisabled = isGenerating || prompt.trim() === "";

  const errorMessages: Record<Exclude<MusicError, "quota_exceeded" | "playback_failed">, string> = {
    empty_prompt: t("errors.empty_prompt"),
    generate_failed: t("errors.generate_failed"),
    load_failed: t("errors.load_failed"),
  };
  const errorMessage = error && error !== "quota_exceeded" && error !== "playback_failed" ? errorMessages[error] : null;

  const footerProps: React.ComponentProps<typeof Footer> =
    errorMessage !== null
      ? {
          children: errorMessage,
          color: "red",
          icon: IconExclamationTriangle,
        }
      : track
        ? {
            icon: IconAudio,
            link: {
              href: continueLink,
              eventName: EventName.marketing_website_music_demo_continue_editing,
              eventProperties: { songId: track.id },
              target: "_blank",
              rel: "noopener noreferrer",
            },
            children: tMusicDemo("claim_song_long"),
          }
        : { children: null };

  return (
    <APIDemo
      code={code}
      codeLanguage={codeLanguage}
      onChangeCodeLanguage={onChangeCodeLanguage}
      showLimitReachedOverlay={showLimitReachedOverlay}
      onCloseLimitReachedOverlay={() => {
        setError(null);
      }}
      limitReachedOverlayEventProps={{ product: "music_api" }}
      copyText={code[codeLanguage as keyof typeof code].code.replace(
        "__TEXT__",
        prompt.replaceAll(
          code[codeLanguage as keyof typeof code].quotes === "single" ? "'" : '"',
          `\\${code[codeLanguage as keyof typeof code].quotes === "single" ? "'" : '"'}`,
        ),
      )}
      primaryButtonIsDisabled={primaryButtonIsDisabled}
      onPrimaryButtonClick={handlePrimaryButtonClick}
      state={apidemoState}
      tokenClassName={token => clsx(token.content.includes("__TEXT__") ? "tw-min-w-0 tw-flex" : "")}
      renderCodePlaceholder={(name, { isActive }) => {
        if (name === "TEXT") {
          return (
            <TextInput
              value={prompt}
              onChange={onChangePrompt}
              isDisabled={isGenerating || !isActive}
              aria-label={t("api.prompt_label")}
            />
          );
        }
      }}
      footerProps={footerProps}
    />
  );
}
